import { FC, createElement, Fragment, type ReactNode } from "react";
import DOMPurify from "dompurify";
import cn from "classnames";
import type { ElementNode, ListItemNode, ListNode, PairNode, ParsedNode, RawNode, TableNode, TextNode } from "src/htmlParser";

const VOID_TAGS = new Set(["img", "hr", "input", "br", "wbr"]);
const SAFE_URL = /^(https?:|mailto:|tel:|\/|#)/i;

type LevelClass = (level: number) => string | undefined;

function safeAttrs(attrs: ElementNode["attrs"]) {
  if (!attrs) return undefined;
  const out: Record<string, string | number> = {};
  Object.entries(attrs).forEach(([name, value]) => {
    if ((name === "href" || name === "src") && !SAFE_URL.test(String(value).trim())) return;
    out[name] = value;
  });
  return out;
}
const InlineHtml: FC<{ html: string; className?: string; style?: ListNode["labelStyle"] }> = ({ html, className, style }) => {
  return <span className={className} style={style} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
};
const Pair: FC<{ pair: PairNode; html?: boolean; className?: string }> = ({ pair, html, className }) => {
  const part = (content: string, partClassName?: string, style?: ListNode["labelStyle"]) =>
    html ? <InlineHtml html={content} className={partClassName} style={style} /> : <span className={partClassName} style={style}>{content}</span>;
  return (
    <span className={cn( className, pair.className)} style={pair.style}>
      {pair.label && part(`${pair.label}:`, pair.labelClassName, pair.labelStyle)}
      {pair.label && pair.value && " "}
      {pair.value && part(pair.value, pair.valueClassName, pair.valueStyle)}
    </span>
  );
};

const Label: FC<{ html: string; pair?: PairNode; className?: string; style?: ListNode["labelStyle"] }> = ({ html, pair, className, style }) => {
  if (!html) return null;
  if (pair) {
    return (
      <span className={cn( className)} style={style}>
        <Pair pair={pair} html />
      </span>
    );
  }
  return <InlineHtml html={html} className={cn( className)} style={style} />;
};

function renderTable(node: TableNode, key: number, lv: LevelClass) {
  return (
    <table key={key} className={cn(lv(node.level), node.className)} style={node.style}>
      <tbody>
        {node.rows.map((row, r) => (
          <tr key={r} className={row.className} style={row.style}>
            {row.cells.map((cell, c) =>
              createElement(
                cell.type,
                { key: c, colSpan: cell.colSpan, rowSpan: cell.rowSpan, className: cn("whitespace-pre-line", cell.className), style: cell.style },
                cell.pair ? <Pair pair={cell.pair} /> : cell.text,
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function renderListItem(item: ListItemNode, key: number, lv: LevelClass) {
  return (
    <li key={key} className={cn(lv(item.level), item.className)} style={item.style}>
      <Label html={item.label} pair={item.pair} className={item.labelClassName} style={item.labelStyle} />
      {item.children.map((sub, i) => renderList(sub, i, lv))}
    </li>
  );
}

function renderList(node: ListNode, key: number, lv: LevelClass) {
  return (
    <Fragment key={key}>
      <Label html={node.label} pair={node.pair} className={cn(lv(node.level), node.labelClassName)} style={node.labelStyle} />
      {createElement(
        node.type,
        { className: cn(lv(node.level), node.className), style: node.style },
        node.children.map((item, i) => renderListItem(item, i, lv)),
      )}
    </Fragment>
  );
}

function renderNode(node: ParsedNode, key: number, lv: LevelClass): ReactNode {
  if (node.type === "text") return <Fragment key={key}>{(node as TextNode).text}</Fragment>;
  if (node.type === "raw")
    return <div key={key} className={lv(node.level)} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((node as RawNode).html) }} />;
  if (node.type === "pair") return <Pair key={key} pair={node as PairNode} className={lv(node.level)} />;
  if (node.type === "table") return renderTable(node as TableNode, key, lv);
  if (node.type === "ul" || node.type === "ol") return renderList(node as ListNode, key, lv);

  const el = node as ElementNode;
  const props = { key, className: cn(lv(el.level), el.className) || undefined, style: el.style, ...safeAttrs(el.attrs) };
  if (VOID_TAGS.has(el.type)) return createElement(el.type, props);
  return createElement(
    el.type,
    props,
    el.children.map((child, i) => renderNode(child, i, lv)),
  );
}

interface ParsedHtmlProps {
  nodes: ParsedNode[];
  className?: string;
  levelClassNames?: string[];
}

const ParsedHtml: FC<ParsedHtmlProps> = ({ nodes, className, levelClassNames }) => {
  const lv: LevelClass = (level) => levelClassNames?.[Math.min(level, levelClassNames.length - 1)];
  return (
    <div dir="auto" className={className}>
      {nodes.map((node, i) => renderNode(node, i, lv))}
    </div>
  );
};

export default ParsedHtml;
